import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { message } from 'antd';
import { FiatResponse, EditingItem } from '../types/api.types';
import ApiService from '../services/api';

interface DataContextType {
  usedFiats: string[];
  allFiats: string[];
  fiatSettings: Record<string, FiatResponse>;
  loading: boolean;
  editingItem: EditingItem | null;
  refreshData: () => Promise<void>;
  setEditingItem: (item: EditingItem | null) => void;
  loadFiatSettings: (fiatName: string) => Promise<void>;
  removeFiatFromCache: (fiatName: string) => void;
  isAuthenticated: boolean;
  login: (password: string) => Promise<void>;
  logout: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [usedFiats, setUsedFiats] = useState<string[]>([]);
  const [allFiats, setAllFiats] = useState<string[]>([]);
  const [fiatSettings, setFiatSettings] = useState<Record<string, FiatResponse>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<EditingItem | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(ApiService.isAuthenticated());

  // Загрузка всех данных
  const refreshData = useCallback(async () => {
    if (!ApiService.isAuthenticated()) return;
    
    setLoading(true);
    try {
      console.log('🚀 Загрузка реальных данных из API...');
      const [usedFiatsData, allFiatsData] = await Promise.all([
        ApiService.getUsedFiats(),
        ApiService.getAllFiats(),
      ]);

      // Валидация полученных данных
      if (!Array.isArray(usedFiatsData) || !Array.isArray(allFiatsData)) {
        console.error('❌ API вернул некорректный формат данных. Ожидались массивы.', {
          usedFiats: usedFiatsData,
          allFiats: allFiatsData,
        });
        // Бросаем ошибку, чтобы перейти в блок catch и загрузить демо-данные
        throw new Error('Некорректный формат данных от API');
      }

      console.log('📊 Полученные данные:', { usedFiatsData, allFiatsData });

      setUsedFiats(usedFiatsData);
      setAllFiats(allFiatsData);

      if (usedFiatsData.length > 0) {
        console.log('⚙️ Загружаем настройки для валют:', usedFiatsData);
        const settingsPromises = usedFiatsData.map(async (fiatName) => {
          try {
            const settings = await ApiService.getFiatSettings(fiatName);
            // Дополнительная валидация для настроек
            if (!settings || typeof settings !== 'object' || !settings.name) {
                 console.warn(`⚠️ Некорректные настройки для ${fiatName}`, settings);
                 return null;
            }
            console.log(`✅ Настройки для ${fiatName}:`, settings);
            return { fiatName, settings };
          } catch (error) {
            console.error(`❌ Ошибка загрузки настроек для ${fiatName}:`, error);
            message.error(`Не удалось загрузить настройки для ${fiatName}`);
            return null;
          }
        });

        const settingsResults = await Promise.all(settingsPromises);
        const newFiatSettings: Record<string, FiatResponse> = {};
        settingsResults.forEach((result) => {
          if (result) {
            newFiatSettings[result.fiatName] = result.settings;
          }
        });

        setFiatSettings(newFiatSettings);
        console.log('🎯 Итоговые настройки сохранены в state:', newFiatSettings);
      } else {
        console.log('ℹ️ Используемые валюты не найдены');
        setFiatSettings({}); // Очищаем старые настройки если они были
      }
    } catch (error) {
      console.error('❌ Произошла ошибка при загрузке данных из API.', error);
      message.error('Не удалось подключиться к API. Загружены демонстрационные данные.');

      console.log('🔄 Загрузка демонстрационных данных...');
      // Устанавливаем демо-данные для тестирования интерфейса
      setUsedFiats(['USD']);
      setAllFiats(['USD', 'EUR', 'RUB', 'BTC', 'ETH']);
      setFiatSettings({
        'USD': {
          id: 1,
          name: 'USD',
          exchange_soups: [
            {
              id: 1,
              name: 'USD Суп #1',
              average: 100.5,
              exchanges: [
                {
                  id: 1,
                  exchange: { id: 1, name: 'Binance' },
                  banks: [
                    { id: 1, name: 'Сбербанк' },
                    { id: 2, name: 'ВТБ' },
                    { id: 3, name: 'Тинькофф' }
                  ],
                  filter_buy: { id: 1, month_order: 10, month_finish_rate: 95.5, max_low_single_trans_amount: 1000, min_high_single_trans_amount: 10000, average_size: 5000 },
                  filter_sell: { id: 2, month_order: 8, month_finish_rate: 92.0, max_low_single_trans_amount: 800, min_high_single_trans_amount: 8000, average_size: 4000 }
                }
              ]
            }
          ]
        }
      });
      console.log('✅ Демо-данные установлены.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка настроек для конкретной валюты
  const loadFiatSettings = useCallback(async (fiatName: string) => {
    try {
      const settings = await ApiService.getFiatSettings(fiatName);
      setFiatSettings(prev => ({
        ...prev,
        [fiatName]: settings
      }));
    } catch (error) {
      console.error(`Ошибка загрузки настроек для ${fiatName}:`, error);
      message.error(`Не удалось загрузить настройки для ${fiatName}`);
    }
  }, []);

  // Удаление валюты из кэша
  const removeFiatFromCache = useCallback((fiatName: string) => {
    setUsedFiats(prev => prev.filter(name => name !== fiatName));
    setFiatSettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[fiatName];
      return newSettings;
    });
  }, []);

  const login = useCallback(async (password: string) => {
    await ApiService.login(password);
    setIsAuthenticated(true);
    await refreshData();
  }, [refreshData]);

  const logout = useCallback(() => {
    ApiService.logout();
    setIsAuthenticated(false);
    setUsedFiats([]);
    setFiatSettings({});
  }, []);

  const value: DataContextType = {
    usedFiats,
    allFiats,
    fiatSettings,
    loading,
    editingItem,
    refreshData,
    setEditingItem,
    loadFiatSettings,
    removeFiatFromCache,
    isAuthenticated,
    login,
    logout
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

// Хук для использования контекста
export const useDataContext = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};
